import { useId, useState } from 'react'

import { FxAlert } from '@/components/fx'
import {
  DEFAULT_THRESHOLD,
  MAX_THRESHOLD,
  MIN_THRESHOLD,
  formatThreshold,
  readDefaultThreshold,
  writeDefaultThreshold,
} from './defaultThreshold'

/**
 * The "Auswertung" tab: the confidence threshold a newly created form
 * starts with.
 *
 * The promise this screen makes is kept in exactly one place: the funnel's
 * `AnalysePage` reads `readDefaultThreshold()` right after
 * `templatesApi.upload` and PATCHes `confidenceThreshold` when it differs
 * from the gateway's own 0.8. There is no user-level column for it, so any
 * further path that creates a template has to do the same — otherwise the
 * sentence under the slider becomes a lie again. `defaultThreshold.test.ts`
 * guards that at least one creating path still reads the value.
 *
 * A native `<input type="range">` rather than a drawn slider — it is
 * keyboard-operable, announces its value, and honours the platform's own
 * pointer behaviour, none of which a div with a drag handler gets for free.
 * The percentage beside it is a second reading of the same value for anyone
 * who cannot judge a handle's position.
 *
 * Saved on change rather than behind a button: there is nothing to fail, and
 * a "Speichern" for a browser-local number would suggest it travels further
 * than it does.
 */
export function AnalysisSection() {
  const sliderId = useId()
  const [threshold, setThreshold] = useState(readDefaultThreshold)

  function onChange(value: number) {
    setThreshold(value)
    writeDefaultThreshold(value)
  }

  return (
    <section className="flex max-w-[640px] flex-col gap-6">
      <h2 className="text-tile-title text-text">Standardwert für neue Formulare</h2>
      <p className="text-body text-text">
        Felder, die unter diesem Wert erkannt werden, markieren wir zur Prüfung. Je höher der Wert,
        desto mehr Felder sehen Sie noch einmal durch.
      </p>

      <div className="flex flex-col gap-4 rounded-sm bg-surface p-5 shadow-raised">
        <label htmlFor={sliderId} className="text-body-strong text-text">
          Schwellwert für neue Formulare
        </label>
        <div className="flex items-center gap-5">
          <input
            id={sliderId}
            type="range"
            className="h-control flex-grow accent-primary-strong"
            min={MIN_THRESHOLD * 100}
            max={MAX_THRESHOLD * 100}
            step={1}
            value={Math.round(threshold * 100)}
            // The slider works in whole percent; the stored value is the
            // 0..1 fraction the gateway keeps on a template.
            onChange={(event) => onChange(Number(event.target.value) / 100)}
          />
          <output htmlFor={sliderId} className="w-16 text-right text-body-strong text-text">
            {formatThreshold(threshold)}
          </output>
        </div>
        <p className="text-small text-text-muted">
          Voreinstellung: {formatThreshold(DEFAULT_THRESHOLD)}. Der Wert gilt für Formulare, die Sie
          ab jetzt in diesem Browser anlegen; bereits angelegte Formulare behalten ihren eigenen
          Schwellwert.
        </p>
      </div>

      <FxAlert tone="info" title="Nur in diesem Browser gespeichert">
        <p>
          Für diese Voreinstellung gibt es noch keinen Platz in Ihrem Konto. Sie gilt deshalb auf
          diesem Gerät und in diesem Browser — auf einem anderen Rechner stellen Sie sie erneut ein.
        </p>
      </FxAlert>
    </section>
  )
}
